import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import {
  persistStore,
  persistReducer,
  FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER
} from 'redux-persist';

// Import reducers
import authReducer from './slices/auth.js';   // Revert to .js extension
import jobsReducer from './slices/jobs.js';   // Revert to .js extension
import themeReducer from './slices/theme.js'; // Revert to .js extension
import uiReducer from './slices/ui.js';     // Revert to .js extension
import warehouseWorkerReducer from './slices/warehouseWorker.js'; // Revert to .js extension
// Removed counterReducer import

const noopStorage = {
  getItem: (_key: string) => Promise.resolve(null),
  setItem: (_key: string, _value: string) => Promise.resolve(),
  removeItem: (_key: string) => Promise.resolve()
};

const isServer = typeof window === 'undefined';
const storage = isServer
  ? noopStorage
  : {
      getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
      setItem: (key: string, value: string) => Promise.resolve(localStorage.setItem(key, value)),
      removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
    };

const authPersistConfig = {
  key: 'auth',
  storage,
  serialize: true,
  deserialize: true
};

const themePersistConfig = {
  key: 'theme',
  storage,
  serialize: true,
  deserialize: true
};

// Removed counterPersistConfig

function makeRootReducer(persist: boolean) {
  return combineReducers({
    theme: persist ? persistReducer(themePersistConfig, themeReducer) : themeReducer,
    // Do NOT persist auth: rely on Supabase client for session state
    auth: authReducer,
    ui: uiReducer,
    jobs: jobsReducer, // Add jobsReducer
    warehouseWorker: warehouseWorkerReducer
    // Removed counter reducer entry
  });
}

const exampleReducer = makeRootReducer(true); // Use true or false, doesn't matter for state shape inference
export type RootState = ReturnType<typeof exampleReducer>;

export function createStore(preloadedState?: Partial<RootState>) {
  const isServer = typeof window === 'undefined';

  const rootReducer = makeRootReducer(!isServer);

  return configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState as any,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          ignoredPaths: ['auth.session'],
        },
      }),
    devTools: import.meta.env.DEV,
  });
}

// Create a function to create the persistor
export function createPersistor(store: ReturnType<typeof createStore>) {
  return persistStore(store);
}

// For type inference with thunk support
export type AppStore = ReturnType<typeof createStore>;
export type AppDispatch = AppStore['dispatch'] & import('@reduxjs/toolkit').ThunkDispatch<RootState, unknown, import('@reduxjs/toolkit').AnyAction>;
